import React, { useState, useMemo, useCallback } from 'react';
import { Card, Form, DatePicker, InputNumber, Button, Table, Space, Collapse, Divider, message, Popconfirm, Typography, Row, Col } from 'antd';
import { PlusOutlined, DeleteOutlined, SaveOutlined, CopyOutlined, ClearOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { 
  useMonthlyTargets, 
  useMassSetMonthlyTargets, 
  useUpdateMonthlyTarget, 
  useDeleteMonthlyTargets,
  type RevenueItem 
} from '@/services';

const { Title } = Typography;
const { Panel } = Collapse;

// Target item structure based on RevenueItem with monthly target type
interface TargetItem extends RevenueItem {
  type: 'monthly_target';
  month: number; // Derived from date field
  year: number;  // Derived from date field
}

interface TargetSetupTabProps {
  // Add any props if needed from parent component
}

const TargetSetupTab: React.FC<TargetSetupTabProps> = () => {
  const [selectedYear, setSelectedYear] = useState<Dayjs>(dayjs());
  const [editingKey, setEditingKey] = useState<number | null>(null);
  const [editingAmount, setEditingAmount] = useState<number>(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [bulkFillValue, setBulkFillValue] = useState<number>();
  const [form] = Form.useForm();

  // API hooks
  const { data: rawTargets = [], isLoading, refetch } = useMonthlyTargets(selectedYear.year());
  const massSetMutation = useMassSetMonthlyTargets();
  const updateMutation = useUpdateMonthlyTarget();
  const deleteMutation = useDeleteMonthlyTargets();

  // Transform API data to include month/year derived from date
  const targets = useMemo((): TargetItem[] => {
    return rawTargets.map(target => {
      const date = dayjs(target.date);
      return {
        ...target,
        type: 'monthly_target' as const,
        month: date.month() + 1, // dayjs months are 0-based, we want 1-based
        year: date.year(),
      };
    });
  }, [rawTargets]);

  const loading = isLoading || massSetMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  // Month names for display
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Currency formatters (matching BaselineForecast pattern)
  const currencyFormatter = useCallback((value: any) => {
    if (!value) return '';
    return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }, []);

  const currencyFormatterWithSymbol = useCallback((value: any) => {
    if (!value) return '';
    return `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }, []);

  const currencyParser = useCallback((value: any) => {
    return Number(value?.replace(/\$\s?|(,*)/g, '')) || 0;
  }, []);

  // Handle year change
  const handleYearChange = (date: Dayjs | null) => {
    if (date) {
      setSelectedYear(date);
      setEditingKey(null);
      setSelectedRowKeys([]);
      // Data will be automatically refetched by the useMonthlyTargets hook
    }
  };

  // Handle mass set targets from form
  const handleMassSet = async (values: any) => {
    const year = selectedYear.year();
    const targetsToSet: Array<{ year: number; month: number; amount: number }> = [];

    // Collect all non-empty month values
    for (let month = 1; month <= 12; month++) {
      const amount = values[`month_${month}`];
      if (amount && amount > 0) {
        targetsToSet.push({ year, month, amount });
      }
    }

    if (targetsToSet.length === 0) {
      message.warning('Please enter at least one target amount');
      return;
    }

    try {
      await massSetMutation.mutateAsync(targetsToSet);
      message.success(`Successfully set targets for ${targetsToSet.length} month(s)`);
      
      // Clear form
      form.resetFields();
    } catch (error) {
      console.error('Failed to set targets:', error);
      message.error('Failed to set targets');
    }
  };

  // Handle quick fill buttons (matching BaselineForecast pattern)
  const handleBulkFill = useCallback(() => {
    if (!bulkFillValue) {
      message.warning('Please enter a value to fill');
      return;
    }
    
    const formData: any = {};
    for (let month = 1; month <= 12; month++) {
      formData[`month_${month}`] = bulkFillValue;
    }
    form.setFieldsValue(formData);
  }, [bulkFillValue, form]);

  const handleClearAll = useCallback(() => {
    form.resetFields();
    setBulkFillValue(undefined);
  }, [form]);

  // Handle single target edit
  const handleEdit = (record: TargetItem) => {
    setEditingKey(record.id);
    setEditingAmount(record.amount);
  };

  const handleSaveEdit = async () => {
    if (editingKey === null) return;

    const target = targets.find(t => t.id === editingKey);
    if (!target) return;

    try {
      await updateMutation.mutateAsync({
        id: editingKey,
        amount: editingAmount,
        year: target.year,
        month: target.month,
      });
      
      setEditingKey(null);
      message.success('Target updated successfully');
    } catch (error) {
      console.error('Failed to update target:', error);
      message.error('Failed to update target');
    }
  };

  const handleCancelEdit = () => {
    setEditingKey(null);
    setEditingAmount(0);
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select targets to delete');
      return;
    }

    try {
      await deleteMutation.mutateAsync(selectedRowKeys as number[]);
      setSelectedRowKeys([]);
      message.success(`Successfully deleted ${selectedRowKeys.length} target(s)`);
    } catch (error) {
      console.error('Failed to delete targets:', error);
      message.error('Failed to delete targets');
    }
  };

  // Table columns
  const columns = [
    {
      title: 'Month',
      dataIndex: 'month',
      key: 'month',
      width: 150,
      render: (month: number) => monthNames[month - 1],
    },
    {
      title: 'Year',
      dataIndex: 'year',
      key: 'year',
      width: 100,
    },
    {
      title: 'Target Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 200,
      render: (amount: number, record: TargetItem) => {
        if (editingKey === record.id) {
          return (
            <InputNumber
              value={editingAmount}
              onChange={(value) => setEditingAmount(value || 0)}
              onPressEnter={handleSaveEdit}
              onBlur={handleSaveEdit}
              style={{ width: '100%' }}
              formatter={currencyFormatter}
              parser={currencyParser}
              min={0}
              step={1000}
              precision={2}
              autoFocus
            />
          );
        }
        return (
          <span
            onClick={() => handleEdit(record)}
            style={{ cursor: 'pointer', padding: '4px 8px', borderRadius: '4px' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f5f5f5';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            {amount.toLocaleString()}
          </span>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: any, record: TargetItem) => (
        <Space>
          {editingKey === record.id ? (
            <>
              <Button
                type="link"
                size="small"
                icon={<SaveOutlined />}
                onClick={handleSaveEdit}
                loading={loading}
              >
                Save
              </Button>
              <Button
                type="link"
                size="small"
                onClick={handleCancelEdit}
              >
                Cancel
              </Button>
            </>
          ) : (
            <Button
              type="link"
              size="small"
              onClick={() => handleEdit(record)}
            >
              Edit
            </Button>
          )}
        </Space>
      ),
    },
  ];

  // Row selection configuration
  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      {/* Target Setup Form */}
      <Collapse size="small">
        <Panel header="Monthly Target Setup" key="1">
          <Form
            form={form}
            onFinish={handleMassSet}
            layout="vertical"
          >
            <Row gutter={16} align="middle" style={{ marginBottom: 16 }}>
              <Col span={6}>
                <Form.Item label="Year" style={{ marginBottom: 0 }}>
                  <DatePicker
                    value={selectedYear}
                    onChange={handleYearChange}
                    picker="year"
                    style={{ width: '100%' }}
                    allowClear={false}
                  />
                </Form.Item>
              </Col>
              <Col span={18}>
                <Typography.Text strong>Quick Fill Options</Typography.Text>
                <Space style={{ width: '100%', marginTop: 4 }}>
                  <InputNumber
                    placeholder="Amount to fill all"
                    value={bulkFillValue}
                    onChange={(value) => setBulkFillValue(value ?? undefined)}
                    min={0}
                    step={1000}
                    precision={2}
                    style={{ width: 200 }}
                    formatter={currencyFormatterWithSymbol}
                    parser={currencyParser}
                  />
                  <Button
                    icon={<CopyOutlined />}
                    onClick={handleBulkFill}
                    disabled={!bulkFillValue}
                    size="small"
                  >
                    Fill All
                  </Button>
                  <Button
                    icon={<ClearOutlined />}
                    onClick={handleClearAll}
                    size="small"
                  >
                    Clear All
                  </Button>
                </Space>
              </Col>
            </Row>

            <Divider orientation="left" style={{ margin: '16px 0' }}>
              Monthly Targets for {selectedYear.year()}
            </Divider>

            <Row gutter={[16, 16]}>
              {monthNames.map((monthName, index) => (
                <Col span={6} key={index + 1}>
                  <Form.Item
                    label={monthName}
                    name={`month_${index + 1}`}
                    style={{ marginBottom: 8 }}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      placeholder="Enter target"
                      formatter={currencyFormatter}
                      parser={currencyParser}
                      min={0}
                      step={1000}
                      precision={2}
                    />
                  </Form.Item>
                </Col>
              ))}
            </Row>

            <Form.Item style={{ marginTop: 16, marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                icon={<PlusOutlined />}
                loading={loading}
              >
                Set Monthly Targets
              </Button>
            </Form.Item>
          </Form>
        </Panel>
      </Collapse>

      {/* Existing Targets Table */}
      <Card 
        title={`Monthly Targets for ${selectedYear.year()}`}
        size="small"
        extra={
          selectedRowKeys.length > 0 && (
            <Popconfirm
              title={`Are you sure you want to delete ${selectedRowKeys.length} selected target(s)?`}
              onConfirm={handleBulkDelete}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="primary"
                danger
                size="small"
                icon={<DeleteOutlined />}
                loading={loading}
              >
                Delete Selected ({selectedRowKeys.length})
              </Button>
            </Popconfirm>
          )
        }
      >
        <Table
          columns={columns}
          dataSource={targets}
          rowKey="id"
          loading={loading}
          pagination={false}
          size="small"
          scroll={{ y: 400 }}
          rowSelection={rowSelection}
          locale={{
            emptyText: `No targets found for ${selectedYear.year()}. Use the form above to add targets.`
          }}
        />
      </Card>
    </Space>
  );
};

export default TargetSetupTab;
